import { useState, useEffect, useCallback } from "react";
import { listPortalCommunities } from "services/communities.service";
import {
  getAmenityMasterList,
  getActivityMasterList,
} from "services/masterData.service";
import { listPortalLocations } from "services/locations.service";
import { getServiceAccount } from "services/serviceAccount.service";
import { renderErrorNotifications } from "helpers/error.helpers";

/**
 * Loads all reference data needed for the event creation form in parallel.
 * Returns { communities, amenities, activities, locations, canWaivePlatformFee, loading }.
 *
 * communities → [{ community_id, name, display_name }]
 * amenities   → [{ amenity_code, amenity_name, media: [{ type, value, is_primary }] }]
 * activities  → [{ activity_code, activity_name, media: [{ type, value, is_primary }] }]
 * locations   → [{ id, location_name, city, state, address_line_1 }]
 * canWaivePlatformFee → whether ShoUp allows this vendor to switch off the
 *   customer platform fee: true / false, or null while loading or when the
 *   service account can't be read. Read fresh from the service account (the
 *   persisted auth store can be out of date).
 */
export default function useEventFormData() {
  const [communities, setCommunities] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [canWaivePlatformFee, setCanWaivePlatformFee] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [commRes, amenRes, actRes, locRes, accountRes] = await Promise.all([
      listPortalCommunities({
        is_active: true,
        verification_status: "VERIFIED",
        include_statistics: false,
        page_size: 100,
      }),
      getAmenityMasterList(),
      getActivityMasterList(),
      listPortalLocations(),
      getServiceAccount(),
    ]);
    setLoading(false);

    if (commRes.errors?.length > 0) renderErrorNotifications(commRes.errors);
    else setCommunities(commRes.data?.data ?? []);

    if (amenRes.errors?.length > 0) renderErrorNotifications(amenRes.errors);
    else setAmenities(amenRes.data?.data ?? []);

    if (actRes.errors?.length > 0) renderErrorNotifications(actRes.errors);
    else setActivities(actRes.data?.data ?? []);

    if (locRes.errors?.length > 0) renderErrorNotifications(locRes.errors);
    else setLocations(locRes.data?.data ?? []);

    // No notification on error: the permission stays unknown (null), so the
    // switch stays hidden and nothing is changed on the vendor's behalf.
    setCanWaivePlatformFee(
      accountRes.errors?.length > 0 || !accountRes.data?.vendor
        ? null
        : accountRes.data.vendor.can_waive_platform_fee === true
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    communities,
    amenities,
    activities,
    locations,
    canWaivePlatformFee,
    loading,
  };
}
